import React, { useEffect, useRef, useState } from "react";
import { X, Twitter, Link2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { fmtUsd, fmtPrice } from "@/lib/format";
import { SITE_URL } from "@/lib/socials";

function blobToDataURL(blob) {
  return new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(blob); });
}

function loadImg(src) {
  return new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
}

export default function ShareCardModal({ coin, suiUsd = 2.15, onClose }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(true);
  const coinUrl = `${SITE_URL}/coin/${coin.curve_id}`;
  const shareUrl = `${SITE_URL}/api/c/${coin.curve_id}`;
  const mcUsd = (coin.market_cap_sui || 0) * suiUsd;
  const progressPct = Math.round((coin.progress || 0) * 100);

  useEffect(() => {
    let alive = true;
    (async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const W = 1200, Hc = 630;
      canvas.width = W; canvas.height = Hc;
      const ctx = canvas.getContext("2d");

      // background
      const bg = ctx.createLinearGradient(0, 0, W, Hc);
      bg.addColorStop(0, "#080B12"); bg.addColorStop(0.5, "#0C1220"); bg.addColorStop(1, "#080B12");
      ctx.fillStyle = bg; ctx.fillRect(0, 0, W, Hc);
      // grid glow
      ctx.strokeStyle = "rgba(0,255,135,0.06)"; ctx.lineWidth = 1;
      for (let x = 0; x < W; x += 48) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, Hc); ctx.stroke(); }
      for (let y = 0; y < Hc; y += 48) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }
      const glow = ctx.createRadialGradient(W - 200, 140, 40, W - 200, 140, 420);
      glow.addColorStop(0, "rgba(124,242,78,0.18)"); glow.addColorStop(1, "rgba(124,242,78,0)");
      ctx.fillStyle = glow; ctx.fillRect(0, 0, W, Hc);

      // brand
      ctx.fillStyle = "#1EC62E"; ctx.font = "800 34px Poppins, sans-serif";
      ctx.fillText("Sui", 72, 84); const sw = ctx.measureText("Sui").width;
      ctx.fillStyle = "#fff"; ctx.fillText("Pads", 72 + sw, 84);
      ctx.fillStyle = "#5b6472"; ctx.font = "600 20px JetBrains Mono, monospace";
      ctx.fillText("LIVE ON SUI MAINNET", 72, 118);

      // logo
      const logoX = 72, logoY = 170, logoS = 150;
      try {
        if (coin.logo_path) {
          const resp = await fetch(`${API}/files/${coin.logo_path}`);
          const blob = await resp.blob();
          const durl = await blobToDataURL(blob);
          const img = await loadImg(durl);
          ctx.save(); roundRect(ctx, logoX, logoY, logoS, logoS, 28); ctx.clip();
          ctx.drawImage(img, logoX, logoY, logoS, logoS); ctx.restore();
        } else throw new Error("no logo");
      } catch (e) {
        ctx.fillStyle = "#1EC62E"; roundRect(ctx, logoX, logoY, logoS, logoS, 28); ctx.fill();
        ctx.fillStyle = "#000"; ctx.font = "800 72px Poppins, sans-serif"; ctx.textAlign = "center";
        ctx.fillText((coin.symbol || "?")[0].toUpperCase(), logoX + logoS / 2, logoY + logoS / 2 + 26); ctx.textAlign = "left";
      }
      ctx.strokeStyle = "rgba(0,255,135,0.5)"; ctx.lineWidth = 3; roundRect(ctx, logoX, logoY, logoS, logoS, 28); ctx.stroke();

      // name + symbol
      const tx = logoX + logoS + 40;
      ctx.fillStyle = "#fff"; ctx.font = "800 60px Poppins, sans-serif";
      ctx.fillText((coin.name || "").slice(0, 16), tx, logoY + 60);
      ctx.fillStyle = "#1EC62E"; ctx.font = "700 40px JetBrains Mono, monospace";
      ctx.fillText(`$${(coin.symbol || "").toUpperCase()}`, tx, logoY + 112);

      // market cap
      ctx.fillStyle = "#5b6472"; ctx.font = "600 22px JetBrains Mono, monospace";
      ctx.fillText("MARKET CAP", 72, 430);
      ctx.fillStyle = "#fff"; ctx.font = "800 64px Poppins, sans-serif";
      ctx.fillText(fmtUsd(mcUsd), 72, 494);
      ctx.fillStyle = "#5b6472"; ctx.font = "600 22px JetBrains Mono, monospace";
      ctx.fillText(`Price ${fmtPrice(coin.price_sui)} SUI`, 72, 528);

      // progress bar to Cetus
      const barX = 72, barY = 560, barW = W - 144, barH = 20;
      ctx.fillStyle = "rgba(255,255,255,0.08)"; roundRect(ctx, barX, barY, barW, barH, 10); ctx.fill();
      const grad = ctx.createLinearGradient(barX, 0, barX + barW, 0);
      grad.addColorStop(0, "#1EC62E"); grad.addColorStop(1, "#1EC62E");
      ctx.fillStyle = grad; roundRect(ctx, barX, barY, Math.max(barW * (coin.progress || 0), 12), barH, 10); ctx.fill();
      ctx.fillStyle = "#5b6472"; ctx.font = "600 20px JetBrains Mono, monospace";
      ctx.fillText(`${progressPct}% to Cetus · graduates at 7,500 SUI`, barX, barY - 12);

      // domain watermark
      ctx.fillStyle = "#1EC62E"; ctx.font = "700 22px JetBrains Mono, monospace";
      ctx.textAlign = "right"; ctx.fillText("suipads.fun", W - 72, 118); ctx.textAlign = "left";

      if (alive) setDrawing(false);
    })();
    return () => { alive = false; };
  }, [coin, mcUsd, progressPct]);

  const shareX = () => {
    const text = `🚀 $${(coin.symbol || "").toUpperCase()} is LIVE on SuiPads — fair-launch memecoin on a Sui bonding curve!\nMarket cap ${fmtUsd(mcUsd)} · ${progressPct}% to Cetus 📈\nApe in 👇`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`, "_blank");
  };
  const copyLink = () => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); };
  const download = () => {
    try {
      const url = canvasRef.current.toDataURL("image/png");
      const a = document.createElement("a"); a.href = url; a.download = `${coin.symbol || "coin"}-suipads.png`; a.click();
      toast.success("Image downloaded");
    } catch (e) { toast.error("Could not export image"); }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-2xl my-8 rounded-2xl border border-white/10 bg-[#0B101D] p-5 relative" onClick={(e) => e.stopPropagation()} data-testid="share-card-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-extrabold text-xl">Share ${(coin.symbol || "").toUpperCase()}</h2>
          <button onClick={onClose} data-testid="share-card-close" className="text-muted-foreground hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="relative rounded-xl overflow-hidden border border-white/10 bg-black">
          {drawing && <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Rendering…</div>}
          <canvas ref={canvasRef} className="w-full h-auto block" data-testid="share-card-canvas" />
        </div>
        <div className="grid grid-cols-3 gap-2 mt-4">
          <button onClick={shareX} data-testid="share-on-x-button" className="flex items-center justify-center gap-2 py-2.5 rounded-lg font-bold text-sm text-black" style={{ background: "linear-gradient(90deg,#1EC62E,#7CF24E)" }}>
            <Twitter className="w-4 h-4" /> Share on X
          </button>
          <button onClick={download} data-testid="share-download-button" className="flex items-center justify-center gap-2 py-2.5 rounded-lg font-bold text-sm border border-white/15 hover:border-[#1EC62E]/50 transition-colors">
            <Download className="w-4 h-4" /> Image
          </button>
          <button onClick={copyLink} data-testid="share-copy-link-button" className="flex items-center justify-center gap-2 py-2.5 rounded-lg font-bold text-sm border border-white/15 hover:border-[#1EC62E]/50 transition-colors">
            <Link2 className="w-4 h-4" /> Copy link
          </button>
        </div>
      </div>
    </div>
  );
}
